import React, { useMemo, useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Tabs } from '@/components/UI/Tabs';
import { StatCard } from '@/components/UI/StatCard';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Alert } from '@/components/UI/Alert';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetPlatformUsageQuery,
  type UsageGroupBy,
  type UsageFilters,
  type UsageRow,
  type UsageTotals,
} from '@/redux/internal-services/platformAdminApi';

const GROUPS: { id: UsageGroupBy; label: string }[] = [
  { id: 'provider_model', label: 'Provider → Model' },
  { id: 'provider', label: 'Provider' },
  { id: 'model', label: 'Model' },
  { id: 'tenant', label: 'Tenant' },
  { id: 'user', label: 'User' },
  { id: 'agent', label: 'Agent' },
  { id: 'mode', label: 'Mode' },
  { id: 'day', label: 'Day' },
  { id: 'run', label: 'Runs' },
  { id: 'total', label: 'Platform' },
];

// Clicking a row narrows to it and moves to the next useful breakdown.
const DRILL: Partial<Record<UsageGroupBy, { filter: keyof UsageFilters; next: UsageGroupBy }>> = {
  provider_model: { filter: 'model', next: 'tenant' },
  provider: { filter: 'provider', next: 'model' },
  model: { filter: 'model', next: 'tenant' },
  tenant: { filter: 'tenant_id', next: 'user' },
  user: { filter: 'user_id', next: 'day' },
  agent: { filter: 'agent_id', next: 'user' },
  mode: { filter: 'interaction_mode', next: 'provider' },
  day: { filter: 'date_from', next: 'run' },
};

const FILTER_LABELS: Record<keyof UsageFilters, string> = {
  tenant_id: 'Tenant', user_id: 'User', provider: 'Provider', model: 'Model',
  agent_id: 'Agent', interaction_mode: 'Mode', date_from: 'From', date_to: 'To',
};

// Groupings whose rows can be broken into vendor -> model.
const SPLITTABLE: UsageGroupBy[] = ['tenant', 'user', 'agent', 'mode', 'day'];

// The pickers hold local date+time; the API takes exact UTC timestamps (the end is exclusive, so +1 minute).
const toIso = (local?: string, plusMs = 0) => (local ? new Date(new Date(local).getTime() + plusMs).toISOString() : undefined);

// Input/output by modality exactly as the provider reported it (voice: text / audio / image, with cached per modality).
const modalityLine = (r: UsageTotals): string | null => {
  const bits: string[] = [];
  const inp = (label: string, n?: number, cached?: number) => {
    if (n) bits.push(`${label} in ${fmt(n)}${cached ? ` (${fmt(cached)} cached)` : ''}`);
  };
  inp('text', r.text_input_tokens, r.cached_text_input_tokens);
  inp('audio', r.audio_input_tokens, r.cached_audio_input_tokens);
  inp('image', r.image_input_tokens, r.cached_image_input_tokens);
  if (r.text_output_tokens) bits.push(`text out ${fmt(r.text_output_tokens)}`);
  if (r.audio_output_tokens) bits.push(`audio out ${fmt(r.audio_output_tokens)}`);
  if (r.billable_audio_seconds) bits.push(`${r.billable_audio_seconds}s billable audio`);
  return bits.length ? bits.join(' · ') : null;
};

const fmt = (n: number) => n.toLocaleString();
const usd = (n?: number) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const Cost: React.FC<{ usdValue?: number; unpriced?: number }> = ({ usdValue, unpriced }) => (
  <>
    {usd(usdValue)}
    {unpriced ? <span className="block text-caption text-warning-text" title="Tokens of models with no price on that day">{fmt(unpriced)} unpriced</span> : null}
  </>
);

const ProviderRow: React.FC<{ provider: string; t: UsageTotals; max: number }> = ({ provider, t, max }) => (
  <tr className="bg-ui-subtle">
    <td className="td-cell">
      <p className="text-sm font-semibold text-ui-text">{provider}</p>
      <p className="text-caption text-ui-text3">all models</p>
    </td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{fmt(t.runs)}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{fmt(t.input_tokens)}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{fmt(t.output_tokens)}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{fmt(t.cached_tokens)}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{t.text_input_tokens ? fmt(t.text_input_tokens) : '—'}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{t.text_output_tokens ? fmt(t.text_output_tokens) : '—'}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{t.audio_input_tokens ? fmt(t.audio_input_tokens) : '—'}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{t.image_input_tokens ? fmt(t.image_input_tokens) : '—'}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{t.audio_output_tokens ? fmt(t.audio_output_tokens) : '—'}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold">{fmt(t.reasoning_tokens ?? 0)}</td>
    <td className="td-cell text-right font-mono text-xs font-bold">{fmt(t.total_tokens)}</td>
    <td className="td-cell text-right font-mono text-xs font-semibold"><Cost usdValue={t.estimated_cost_usd} unpriced={t.unpriced_tokens} /></td>
    <td className="td-cell">
      <div className="h-2 w-full rounded-full bg-ui-hover" aria-hidden>
        <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.max(2, Math.round((t.total_tokens / max) * 100))}%` }} />
      </div>
    </td>
    <td className="td-cell" />
  </tr>
);

const UsagePage: React.FC = () => {
  const [groupBy, setGroupBy] = useState<UsageGroupBy>('provider_model');
  const [filters, setFilters] = useState<UsageFilters>({});
  // Human names for the active filters (the filter value itself is an id).
  const [names, setNames] = useState<Record<string, string>>({});

  const [splitOn, setSplitOn] = useState(true);
  const split = splitOn && SPLITTABLE.includes(groupBy);

  const { data, isLoading, isFetching, isError, error } = useGetPlatformUsageQuery({
    group_by: groupBy, ...(split ? { split: 'vendor_model' as const } : {}), ...filters,
    date_from: toIso(filters.date_from), date_to: toIso(filters.date_to, 60_000),
  });
  const usage = data?.data;
  const rows: UsageRow[] = useMemo(() => usage?.rows ?? [], [usage]);
  const totals = usage?.totals;

  const maxTotal = useMemo(
    () => Math.max(1, ...rows.filter((r) => !r.level || r.level === 'entity' || r.level === 'run').map((r) => r.total_tokens)),
    [rows],
  );
  // Provider -> Model view: a subtotal line per provider, its models beneath it.
  const providerTotals = useMemo(() => {
    const m = new Map<string, UsageTotals>();
    for (const r of rows) {
      const p = r.provider ?? 'unknown';
      const t = m.get(p) ?? { input_tokens: 0, output_tokens: 0, cached_tokens: 0, total_tokens: 0, runs: 0, errors: 0 };
      t.input_tokens += r.input_tokens; t.output_tokens += r.output_tokens; t.cached_tokens += r.cached_tokens;
      t.reasoning_tokens = (t.reasoning_tokens ?? 0) + (r.reasoning_tokens ?? 0);
      t.text_input_tokens = (t.text_input_tokens ?? 0) + (r.text_input_tokens ?? 0);
      t.text_output_tokens = (t.text_output_tokens ?? 0) + (r.text_output_tokens ?? 0);
      t.audio_input_tokens = (t.audio_input_tokens ?? 0) + (r.audio_input_tokens ?? 0);
      t.image_input_tokens = (t.image_input_tokens ?? 0) + (r.image_input_tokens ?? 0);
      t.audio_output_tokens = (t.audio_output_tokens ?? 0) + (r.audio_output_tokens ?? 0);
      t.total_tokens += r.total_tokens; t.runs += r.runs;
      t.estimated_cost_usd = (t.estimated_cost_usd ?? 0) + (r.estimated_cost_usd ?? 0);
      t.unpriced_tokens = (t.unpriced_tokens ?? 0) + (r.unpriced_tokens ?? 0);
      m.set(p, t);
    }
    return m;
  }, [rows]);
  const activeFilters = (Object.keys(filters) as (keyof UsageFilters)[]).filter((k) => filters[k]);
  const drill = DRILL[groupBy];

  const setFilter = (key: keyof UsageFilters, value: string) =>
    setFilters((f) => ({ ...f, [key]: value || undefined }));

  const onRow = (row: UsageRow) => {
    if (!drill) return;
    if (row.level === 'vendor' || row.level === 'model') {
      // narrow to this entity AND its vendor (and model); keep the same grouping so the user stays in context
      const ek = String(row.entity_key ?? 'unknown');
      const next: UsageFilters = { [drill.filter]: ek, provider: row.provider };
      if (row.level === 'model') next.model = row.model;
      const entity = rows.find((r) => r.level === 'entity' && String(r.key) === ek);
      setFilters((f) => ({ ...f, ...next }));
      setNames((n) => ({ ...n, [`${drill.filter}:${ek}`]: entity?.label ?? ek }));
      setGroupBy('run');                                    // vendor / model rows open the individual sessions
      return;
    }
    const value = row.key ?? 'unknown';
    if (groupBy === 'day' && row.key) {                      // a day = that local-time day's range
      setFilters((f) => ({ ...f, date_from: `${row.key}T00:00`, date_to: `${row.key}T23:59` }));
      setGroupBy('run');
      return;
    }
    const next: UsageFilters = { [drill.filter]: String(row.model ?? value) };
    if (groupBy === 'provider_model') next.provider = row.provider;   // a model is only meaningful within its provider
    setFilters((f) => ({ ...f, ...next }));
    setNames((n) => ({ ...n, [`${drill.filter}:${row.model ?? value}`]: row.label }));
    setGroupBy(drill.next);
  };

  const clearAll = () => setFilters({});

  const renderRow = (r: UsageRow) => (
    <tr
      key={String(r.key)}
      className={cn('tr-hover', drill && 'cursor-pointer', r.level === 'vendor' && 'bg-ui-subtle', r.level === 'entity' && 'border-t border-ui-border2')}
      onClick={() => onRow(r)}
      onKeyDown={(e) => { if (drill && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onRow(r); } }}
      tabIndex={drill ? 0 : undefined}
          >
      <td className={cn('td-cell', r.level === 'vendor' && 'pl-6', r.level === 'model' && 'pl-12')}>
        <p className={cn('whitespace-nowrap text-sm text-ui-text', r.level === 'vendor' ? 'font-semibold' : r.level === 'model' ? 'font-normal' : 'font-medium')} title={r.key === null || r.label === 'unknown' ? 'Recorded before provider/model tracking' : undefined}>{r.level === 'run' && r.last_at ? new Date(r.last_at).toLocaleString() : r.label}</p>
        {r.level === 'run' && (
          <p className="text-caption text-ui-text3">
            {[r.user_name, r.interaction_mode, r.provider, r.model, r.status, r.duration_s != null ? `${r.duration_s}s` : null].filter(Boolean).join(' · ')}
          </p>
        )}
        {r.level === 'run' && <p className="font-mono text-micro normal-case text-ui-text3" title="Run id">{r.session_id ?? r.run_id}</p>}
        {modalityLine(r) && <p className="text-caption text-ui-text3">{modalityLine(r)}</p>}
        {(r.tenant_name || r.email) && (
          <p className="text-caption text-ui-text3">
            {[r.email, r.tenant_name].filter(Boolean).join(' · ')}
          </p>
        )}
      </td>
      <td className="td-cell text-right font-mono text-xs">{fmt(r.runs)}</td>
      <td className="td-cell text-right font-mono text-xs">{fmt(r.input_tokens)}</td>
      <td className="td-cell text-right font-mono text-xs">{fmt(r.output_tokens)}</td>
      <td className="td-cell text-right font-mono text-xs">{fmt(r.cached_tokens)}</td>
      <td className="td-cell text-right font-mono text-xs">{r.text_input_tokens ? fmt(r.text_input_tokens) : '—'}</td>
      <td className="td-cell text-right font-mono text-xs">{r.text_output_tokens ? fmt(r.text_output_tokens) : '—'}</td>
      <td className="td-cell text-right font-mono text-xs">{r.audio_input_tokens ? fmt(r.audio_input_tokens) : '—'}</td>
      <td className="td-cell text-right font-mono text-xs">{r.image_input_tokens ? fmt(r.image_input_tokens) : '—'}</td>
      <td className="td-cell text-right font-mono text-xs">{r.audio_output_tokens ? fmt(r.audio_output_tokens) : '—'}</td>
      <td className="td-cell text-right font-mono text-xs">{fmt(r.reasoning_tokens ?? 0)}</td>
      <td className="td-cell text-right font-mono text-xs font-semibold">{fmt(r.total_tokens)}</td>
      <td className="td-cell text-right font-mono text-xs"><Cost usdValue={r.estimated_cost_usd} unpriced={r.unpriced_tokens} /></td>
      <td className="td-cell">
        <div className="h-2 w-full rounded-full bg-ui-subtle" aria-hidden>
          <div
            className="h-2 rounded-full bg-accent"
            style={{ width: `${Math.max(2, Math.round((r.total_tokens / maxTotal) * 100))}%` }}
          />
        </div>
      </td>
      <td className="td-cell text-caption text-ui-text3">
        {r.last_at ? new Date(r.last_at).toLocaleString() : '—'}
      </td>
          </tr>
  );

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          title="Usage"
          subtitle="Token usage exactly as the providers report it, from every chat turn and voice session."
          toolbar={
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="eyebrow shrink-0">Group by</span>
                <div className="no-scrollbar min-w-0 overflow-x-auto">
                  <Tabs
                    variant="segmented"
                    label="Group usage by"
                    tabs={GROUPS.map((g) => ({ id: g.id, label: g.label }))}
                    value={groupBy}
                    onChange={(id) => setGroupBy(id as UsageGroupBy)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">From</span>
                  <input
                    type="datetime-local"
                    className={fieldClass({ size: 'sm' })}
                    value={filters.date_from ?? ''}
                    max={filters.date_to || undefined}
                    onChange={(e) => setFilter('date_from', e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">To</span>
                  <input
                    type="datetime-local"
                    className={fieldClass({ size: 'sm' })}
                    value={filters.date_to ?? ''}
                    min={filters.date_from || undefined}
                    onChange={(e) => setFilter('date_to', e.target.value)}
                  />
                </label>
                {activeFilters.length > 0 && (
                  <Button type="button" variant="link" size="xs" className="mb-2" onClick={clearAll}>
                    Clear filters
                  </Button>
                )}
                {SPLITTABLE.includes(groupBy) && (
                  <Chip selected={splitOn} onClick={() => setSplitOn((v) => !v)}>Vendor → model breakdown</Chip>
                )}
              </div>
              {activeFilters.filter((k) => k !== 'date_from' && k !== 'date_to').length > 0 && (
                <div className="flex flex-wrap items-center gap-2" aria-label="Active filters">
                  {activeFilters
                    .filter((k) => k !== 'date_from' && k !== 'date_to')
                    .map((k) => (
                      <Chip key={k} selected onClick={() => setFilter(k, '')} title="Remove filter">
                        {FILTER_LABELS[k]}: {names[`${k}:${filters[k]}`] ?? filters[k]} ✕
                      </Chip>
                    ))}
                </div>
              )}
            </div>
          }
        />
      }
    >
      {isError && <Alert tone="danger">{getErrorDetail(error) ?? 'Could not load usage.'}</Alert>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
        <StatCard label="Runs" value={totals ? fmt(totals.runs) : '—'} />
        <StatCard label="Input tokens" value={totals ? fmt(totals.input_tokens) : '—'} />
        <StatCard label="Output tokens" value={totals ? fmt(totals.output_tokens) : '—'} />
        <StatCard label="Cached tokens" value={totals ? fmt(totals.cached_tokens) : '—'} />
        <StatCard label="Reasoning tokens" value={totals ? fmt(totals.reasoning_tokens ?? 0) : '—'} />
        <StatCard label="Total (in + out)" value={totals ? fmt(totals.total_tokens) : '—'} tone="accent" />
        <StatCard label="Text in / out" value={totals ? `${fmt(totals.text_input_tokens ?? 0)} / ${fmt(totals.text_output_tokens ?? 0)}` : '—'} />
        <StatCard label="Audio in / out" value={totals ? `${fmt(totals.audio_input_tokens ?? 0)} / ${fmt(totals.audio_output_tokens ?? 0)}` : '—'} />
        <StatCard label="Image in" value={totals ? fmt(totals.image_input_tokens ?? 0) : '—'} />
        <StatCard label="Est. cost" value={totals ? usd(totals.estimated_cost_usd) : '—'} />
      </div>

      {totals && (totals.audio_input_tokens || totals.audio_output_tokens || totals.image_input_tokens || totals.billable_audio_seconds) ? (
        <p className="mt-3 text-caption text-ui-text2" aria-label="Voice usage">
          Voice (as reported): {modalityLine(totals) ?? '—'}
        </p>
      ) : null}

      <div className={cn('mt-4', isFetching && !isLoading && 'opacity-70 transition-opacity')}>
        {isLoading ? (
          <LoadingState label="Loading usage…" className="py-16" />
        ) : groupBy === 'total' ? (
          <EmptyState
            className="rounded-card border border-dashed border-ui-border py-12"
            title="Platform totals are shown above"
            description="Pick a grouping (Provider, Tenant, User…) to break them down."
          />
        ) : rows.length === 0 ? (
          <EmptyState
            className="rounded-card border border-dashed border-ui-border py-12"
            title="No usage recorded for these filters."
            description="Usage appears here after a chat turn or voice session."
          />
        ) : (
          <div className="table-card">
            <div className="no-scrollbar overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead>
                  <tr>
                    <th className="th-cell">{groupBy === 'run' ? 'Run (time · session)' : GROUPS.find((g) => g.id === groupBy)?.label}</th>
                    <th className="th-cell text-right">Runs</th>
                    <th className="th-cell text-right">Input</th>
                    <th className="th-cell text-right">Output</th>
                    <th className="th-cell text-right">Cached</th>
                    <th className="th-cell text-right" title="Text tokens in, as the provider reported (voice sessions; chat has no split)">Text in</th>
                    <th className="th-cell text-right" title="Text tokens out, as the provider reported (voice sessions; chat has no split)">Text out</th>
                    <th className="th-cell text-right" title="Voice audio tokens in, as the provider reported (realtime sessions)">Audio in</th>
                    <th className="th-cell text-right" title="Image input tokens, as the provider reported (voice sessions; chat images are inside Input)">Image in</th>
                    <th className="th-cell text-right" title="Voice audio tokens out, as the provider reported (realtime sessions)">Audio out</th>
                    <th className="th-cell text-right" title="Part of output spent on reasoning, as reported">Reasoning</th>
                    <th className="th-cell text-right">Total</th>
                    <th className="th-cell text-right" title="Estimate: recorded tokens x the price version in force on each run's day (Platform → Pricing)">Est. cost</th>
                    <th className="th-cell w-[18%]">Share</th>
                    <th className="th-cell">Last run</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <React.Fragment key={String(r.key)}>
                      {groupBy === 'provider_model' && (i === 0 || rows[i - 1].provider !== r.provider) && (
                        <ProviderRow provider={r.provider ?? 'unknown'} t={providerTotals.get(r.provider ?? 'unknown')!} max={maxTotal} />
                      )}
                      {renderRow(r)}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <p className="mt-3 text-caption text-ui-text3">
          Counts are what each provider reported for the run. Cached tokens are shown as reported: OpenAI, xAI and Gemini
          count them inside input, Anthropic reports them separately. “unknown” = runs recorded before
          provider/model were tracked.{drill ? ' Click a row to drill into it.' : ''}
        </p>
      </div>
    </PageShell>
  );
};

export default UsagePage;
